import { useAuth0 } from '@auth0/auth0-react';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
    Box,
    Checkbox,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Toolbar,
    Tooltip,
    Typography,
    CircularProgress,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { visuallyHidden } from '@mui/utils';
import axios from 'axios';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSchoolTeachers } from '../../redux/SchoolSlice';
import TitleCase from '../BasicComponents/TitleCase';
import InviteUsers from './InviteUsers';

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order;
        }
        return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
}

const headCells = [
    {
        id: 'firstName',
        numeric: false,
        disablePadding: true,
        label: 'First Name',
    },
    {
        id: 'lastName',
        numeric: false,
        disablePadding: true,
        label: 'Last Name',
    },
    {
        id: 'role',
        numeric: false,
        disablePadding: true,
        label: 'Role',
    },
];

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function EnhancedTableHead(props) {
    const {
        onSelectAllClick,
        order,
        orderBy,
        numSelected,
        rowCount,
        onRequestSort,
    } = props;
    const createSortHandler = property => event => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox">
                    <Checkbox
                        color="primary"
                        indeterminate={
                            numSelected > 0 && numSelected < rowCount
                        }
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{
                            'aria-label': 'select all teachers',
                        }}
                    />
                </TableCell>
                {headCells.map(headCell => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                        data-cy={headCell.id}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc'
                                        ? 'sorted descending'
                                        : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

const EnhancedTableToolbar = props => {
    const { numSelected } = props;

    return (
        <Toolbar
            sx={{
                pl: { sm: 2 },
                pr: { xs: 1, sm: 1 },
                ...(numSelected > 0 && {
                    bgcolor: theme => theme.palette.primary.main,
                }),
            }}
        >
            {numSelected > 0 ? (
                <Typography
                    sx={{ flex: '1 1 100%' }}
                    color={grey[50]}
                    variant="subtitle1"
                    component="div"
                >
                    {numSelected} selected
                </Typography>
            ) : (
                <Typography
                    sx={{ flex: '1 1 100%' }}
                    variant="h6"
                    id="tableTitle"
                    component="div"
                >
                    School Teachers
                </Typography>
            )}

            {numSelected > 0 ? (
                <Stack direction="row">
                    <Typography
                        variant="body1"
                        color="#fafafa"
                        sx={{ margin: 'auto' }}
                    >
                        Delete
                    </Typography>
                    <Tooltip title="Delete User">
                        <IconButton
                            data-cy="delete-user"
                            onClick={() => props.actionUsers('delete')}
                        >
                            <DeleteIcon sx={{ color: grey[50] }} />
                        </IconButton>
                    </Tooltip>
                    <Typography
                        variant="body1"
                        color="#fafafa"
                        sx={{ margin: 'auto' }}
                    >
                        User
                    </Typography>
                    <Tooltip title="Make User">
                        <IconButton onClick={() => props.actionUsers('user')}>
                            <PersonAddIcon sx={{ color: grey[50] }} />
                        </IconButton>
                    </Tooltip>

                    <Typography
                        variant="body1"
                        color="#fafafa"
                        sx={{ margin: 'auto' }}
                    >
                        Admin
                    </Typography>
                    <Tooltip title="Make Admin">
                        <IconButton onClick={() => props.actionUsers('admin')}>
                            <AdminPanelSettingsIcon sx={{ color: grey[50] }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            ) : (
                <div />
            )}
        </Toolbar>
    );
};

export const TeacherList = props => {
    const { getAccessTokenSilently } = useAuth0();
    const dispatch = useDispatch();

    const teachersInSchoolStatus = useSelector(
        state => state.school.getTeacherStatus
    );

    const dense = false;
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('firstName');
    const [selected, setSelected] = React.useState([]);
    const [page, setPage] = React.useState(0);
    //const [dense, setDense] = React.useState(false);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const [teachersInSchool, setTeachersInSchool] = React.useState([]);
    const actionUsers = async type => {
        let teacherState = teachersInSchool;
        let teacherOnLoad = teachersInSchool.filter(user => {
            if (selected.includes(user.pk1)) {
                user.load = true;
                return user;
            } else {
                return user;
            }
        });

        setTeachersInSchool(teacherOnLoad);

        let countOfAdmins = teachersInSchool.filter(
            teacher => teacher.role === 'admin'
        ).length;
        //get list of deletions and check noone deletes
        const countOfDeleteAdmins = teachersInSchool.filter(user =>
            selected.find(t => t === user.pk1 && user.role === 'admin')
        );

        console.log(
            'Admins',
            countOfAdmins,
            'deletionAdmins',
            countOfDeleteAdmins
        );

        if (countOfAdmins > countOfDeleteAdmins.length) {
            const token = await getAccessTokenSilently({
                scope: process.env.REACT_APP_scope,
            });
            const data = { users: selected, type: type };
            await axios
                .put('/approveSchool', data, {
                    baseURL: process.env.REACT_APP_API_GATEWAY,
                    url: '/approveSchool',
                    timeout: 30000,
                    headers: {
                        authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                })
                .then(outcome => {
                    const failure = outcome.data.filter(item => {
                        return item === 'failure';
                    });
                    console.log(JSON.stringify(failure.length));
                    if (failure.length > 0) {
                        setTeachersInSchool(teacherState);
                        props.setNotif(
                            'error',
                            0,
                            'Unknown failure, please refresh'
                        );
                    } else {
                        let tempView = teachersInSchool.filter(user => {
                            if (selected.includes(user.pk1)) {
                                user.role = type;
                                user.load = false;
                                return user;
                            } else {
                                return user;
                            }
                        });
                        setTeachersInSchool(tempView);

                        props.setNotif(
                            'success',
                            6000,
                            `Added these users to your school as ${type}`
                        );
                    }
                })
                .catch(error => {
                    setTeachersInSchool(teacherState);
                    props.setNotif('error', 0, 'Failed to add users');
                });
        } else {
            setTeachersInSchool(teacherState);
            props.setNotif('error', 0, `You cannot remove all admins`);
        }
    };

    useEffect(() => {
        async function dispatchSchoolTeachersFromStore() {
            const token = await getAccessTokenSilently({
                scope: process.env.REACT_APP_scope,
            });
            const response = await axios.get('/getSchoolTeachers?type=all', {
                baseURL: process.env.REACT_APP_API_GATEWAY,
                url: `/getSchoolTeachers`,
                timeout: 30000,
                headers: {
                    authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            setTeachersInSchool(response.data.Message.Data);
        }
        dispatchSchoolTeachersFromStore();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (teachersInSchoolStatus === 'idle') {
            async function dispatchSchoolTeachersFromStore() {
                const token = await getAccessTokenSilently({
                    scope: process.env.REACT_APP_scope,
                });
                const requestData = {
                    'token': token,
                };

                //doesnt seem to work on Safari - need to troubleshoot
                dispatch(getSchoolTeachers(requestData));
            }
            dispatchSchoolTeachersFromStore();
        }
        console.log(
            'teachers in school info',
            JSON.stringify(props.school.teachersInSchool)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teachersInSchoolStatus]);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = event => {
        if (selected.length === 0) {
            const newSelecteds = teachersInSchool.map(n => n.pk1);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, name) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1)
            );
        }

        setSelected(newSelected);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const isSelected = name => selected.indexOf(name) !== -1;

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0
            ? Math.max(0, (1 + page) * rowsPerPage - teachersInSchool.length)
            : 0;

    return (
        <Box sx={{ width: '100%' }}>
            <InviteUsers setNotif={props.setNotif} schoolUser={true} />
            <Paper sx={{ width: '100%', mb: 2 }}>
                <EnhancedTableToolbar
                    numSelected={selected.length}
                    actionUsers={actionUsers}
                />
                <TableContainer>
                    <Table
                        aria-labelledby="tableTitle"
                        size={dense ? 'small' : 'medium'}
                    >
                        <EnhancedTableHead
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={teachersInSchool.length}
                        />
                        <TableBody>
                            {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                   rows.slice().sort(getComparator(order, orderBy)) */}
                            {stableSort(
                                teachersInSchool,
                                getComparator(order, orderBy)
                            )
                                .slice(
                                    page * rowsPerPage,
                                    page * rowsPerPage + rowsPerPage
                                )
                                .map((row, index) => {
                                    const isItemSelected = isSelected(row.pk1);
                                    const labelId = `enhanced-table-checkbox-${index}`;

                                    return (
                                        <TableRow
                                            hover
                                            data-cy="school-row"
                                            onClick={event =>
                                                handleClick(event, row.pk1)
                                            }
                                            role="checkbox"
                                            aria-checked={isItemSelected}
                                            tabIndex={-1}
                                            key={row.pk1}
                                            selected={isItemSelected}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    color="primary"
                                                    checked={isItemSelected}
                                                    inputProps={{
                                                        'aria-labelledby':
                                                            labelId,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell
                                                data-cy="first-name"
                                                component="th"
                                                id={labelId}
                                                scope="row"
                                                padding="none"
                                            >
                                                <TitleCase
                                                    string={row.firstName}
                                                ></TitleCase>
                                            </TableCell>
                                            <TableCell
                                                data-cy="last-name"
                                                align="left"
                                                padding="none"
                                            >
                                                <TitleCase
                                                    string={row.lastName}
                                                ></TitleCase>
                                            </TableCell>
                                            <TableCell
                                                data-cy="school-role"
                                                align="left"
                                                padding="none"
                                                sx={{ position: 'relative' }}
                                            >
                                                <TitleCase string={row.role} />
                                                {row.load && (
                                                    <CircularProgress
                                                        size="35px"
                                                        sx={{
                                                            position:
                                                                'absolute',
                                                            top: 0,
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            {emptyRows > 0 && (
                                <TableRow
                                    style={{
                                        height: (dense ? 33 : 53) * emptyRows,
                                    }}
                                >
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={teachersInSchool.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
};

export default TeacherList;
